// Shared "Quick access" tile, used by every department dashboard.
// Frequency tracking (linkFrequency/trackMenuUsage) lives in RoleBasedApplication.jsx and is
// passed down unchanged regardless of which department dashboard is rendering this card.
import React, { useCallback } from 'react';
import {
    BarChart3, Users, ShoppingCart, TrendingUp, DollarSign, FileText,
    Package, Calculator, CreditCard, Zap,
} from 'lucide-react';

const getIconForItem = (item) => {
    const path = item.Path?.toLowerCase() || '';
    const name = (item.SUBLI || '').toLowerCase();

    if (path.includes('report') || name.includes('report')) return BarChart3;
    if (path.includes('employee') || name.includes('employee')) return Users;
    if (path.includes('purchase') || name.includes('purchase')) return ShoppingCart;
    if (path.includes('sale') || name.includes('sale')) return TrendingUp;
    if (path.includes('balance') || name.includes('balance')) return DollarSign;
    if (path.includes('ledger') || name.includes('ledger')) return FileText;
    if (path.includes('inventory') || name.includes('inventory')) return Package;
    if (path.includes('warehouse') || name.includes('warehouse')) return Package;
    if (path.includes('finance') || name.includes('finance')) return Calculator;
    if (path.includes('account') || name.includes('account')) return CreditCard;
    return FileText;
};

// extraSection lets a dashboard surface a labelled shortcut group below the frequency list —
// built from the user's *real* menuItems (matched by keyword), so it never shows a link the
// role doesn't actually have access to. { title, keywords, limit? }
const FrequentAccessCard = ({ roleData, linkFrequency = {}, trackMenuUsage, onNavigate, extraSection }) => {
    const handleLinkClick = useCallback((linkData) => {
        if (trackMenuUsage) {
            trackMenuUsage(linkData);
        }
        if (onNavigate) {
            onNavigate(linkData.reactRoute, linkData);
        }
    }, [onNavigate, trackMenuUsage]);

    const getAllMenuItemsWithFrequency = () => {
        if (!roleData?.menuItems) return [];

        return roleData.menuItems.map(item => {
            const section = item.LI || 'Other';
            const name = item.SUBLI || item.FirmFunctionalAreaName || 'Unknown';
            const linkKey = `${section}_${name}`;
            const frequency = linkFrequency[linkKey] || 0;

            return {
                id: item.FirmFunctionalAreaId,
                name,
                section,
                path: item.Path,
                icon: getIconForItem(item),
                type: item.Type,
                frequency,
                reactRoute: `${item.Type?.toLowerCase()}/${name.toLowerCase().replace(/\s+/g, '-')}`,
                originalItem: item,
                li: item.LI,
                subli: item.SUBLI,
                url: item.UL,
            };
        });
    };

    const getFrequentlyAccessedLinks = () => {
        const allItems = getAllMenuItemsWithFrequency();

        if (Object.keys(linkFrequency).length === 0) {
            return allItems.slice(0, 8);
        }

        const frequentItems = allItems
            .filter(item => item.frequency > 0)
            .sort((a, b) => b.frequency - a.frequency)
            .slice(0, 8);

        if (frequentItems.length < 8) {
            const unclickedItems = allItems
                .filter(item => item.frequency === 0)
                .slice(0, 8 - frequentItems.length);

            return [...frequentItems, ...unclickedItems];
        }

        return frequentItems;
    };

    const quickAccessLinks = getFrequentlyAccessedLinks();
    const totalClicks = Object.values(linkFrequency).reduce((sum, count) => sum + count, 0);
    const uniqueItemsClicked = Object.keys(linkFrequency).length;
    const totalScreens = roleData?.menuItems?.length || 0;

    const extraLinks = extraSection ? (() => {
        const shownIds = new Set(quickAccessLinks.map((link) => link.id));
        return getAllMenuItemsWithFrequency()
            .filter((item) => {
                if (shownIds.has(item.id)) return false;
                const haystack = `${item.section} ${item.name}`.toLowerCase();
                return extraSection.keywords.some((kw) => haystack.includes(kw.toLowerCase()));
            })
            .slice(0, extraSection.limit || 4);
    })() : [];

    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 hover:shadow-lg dark:hover:shadow-xl transition-all flex flex-col h-full p-4">
            <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Quick access</h3>
                <span className="flex items-center space-x-1 text-[11px] text-gray-500 dark:text-gray-400 flex-shrink-0">
                    <Zap className="w-3 h-3 text-yellow-500" />
                    <span>{totalClicks > 0 ? `${totalClicks} total clicks` : 'No activity yet'}</span>
                </span>
            </div>

            <div className="flex-1 space-y-0.5">
                {quickAccessLinks.map((link, index) => {
                    const IconComponent = link.icon;
                    const isFrequent = link.frequency > 0;
                    const isMostUsed = index === 0 && isFrequent;

                    return (
                        <button
                            key={`${link.id}-${index}`}
                            onClick={() => handleLinkClick(link)}
                            className="w-full flex items-center gap-2.5 px-1.5 py-1.5 rounded-md hover:bg-orange-50 dark:hover:bg-[#0d1b5e]/10 transition-all group text-left"
                            title={`${link.section} - ${link.name}${link.frequency > 0 ? ` (${link.frequency} clicks)` : ''}`}
                        >
                            <div className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 ${isFrequent
                                ? 'bg-[#0d1b5e]/10 dark:bg-orange-400/10'
                                : 'bg-gray-100 dark:bg-gray-700'
                                }`}>
                                <IconComponent className={`w-3.5 h-3.5 ${isFrequent
                                    ? 'text-[#0d1b5e] dark:text-orange-400'
                                    : 'text-gray-400 dark:text-gray-500 group-hover:text-[#0d1b5e] dark:group-hover:text-orange-400'
                                    }`} />
                            </div>
                            <div className="min-w-0 flex-1">
                                <p className="text-xs font-medium truncate leading-tight text-gray-900 dark:text-white">
                                    {link.name}
                                </p>
                                <p className="text-[10px] text-gray-400 dark:text-gray-500 truncate leading-tight">
                                    {link.section}{isMostUsed ? ' · Most used' : ''}
                                </p>
                            </div>
                            {isFrequent && (
                                <span className="min-w-[18px] h-[18px] px-1 bg-[#0d1b5e] dark:bg-orange-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold flex-shrink-0">
                                    {link.frequency}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>

            {extraSection && extraLinks.length > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                    <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1.5 px-1.5">
                        {extraSection.title}
                    </p>
                    <div className="grid grid-cols-2 gap-1.5">
                        {extraLinks.map((link) => (
                            <button
                                key={link.id}
                                onClick={() => handleLinkClick(link)}
                                title={`${link.section} - ${link.name}`}
                                className="px-2 py-1.5 text-xs font-medium text-left truncate text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600 rounded-md hover:border-orange-300 dark:hover:border-orange-600 hover:bg-orange-50 dark:hover:bg-[#0d1b5e]/10 hover:text-[#0d1b5e] dark:hover:text-orange-400 transition-all"
                            >
                                {link.name}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            <div className="mt-3 pt-2 border-t border-gray-200 dark:border-gray-600 text-[11px] text-gray-500 dark:text-gray-400">
                {totalClicks > 0 ? (
                    <div className="flex items-center justify-between">
                        <span className="truncate">Most used: {quickAccessLinks[0]?.name}</span>
                        <span className="flex-shrink-0 ml-2">{uniqueItemsClicked} of {totalScreens} used</span>
                    </div>
                ) : (
                    <div className="text-center">Start clicking menu items to see your most used features!</div>
                )}
            </div>
        </div>
    );
};

export default FrequentAccessCard;
